<template>
  <component
    :is="tag"
    class="play-avatar"
    :class="[`play-avatar--${variant}`, { 'play-avatar--square': square, 'play-avatar--ring': ring }]"
    :style="avatarStyle"
    v-bind="$attrs"
    v-on="$listeners"
  >
    <!-- VUE3-MIGRATION: $listeners removed in Vue 3 (merged into $attrs).
       On migration delete `v-on="$listeners"` — v-bind="$attrs" forwards listeners too. -->
    <img
      v-if="src"
      class="play-avatar__img"
      :src="src"
      :alt="alt"
      loading="lazy"
    >
    <slot v-else>
      <span class="play-avatar__text">{{ text }}</span>
    </slot>
  </component>
</template>

<script>
/**
 * PlayAvatar — circular (or squared) avatar for the Play system.
 *
 * Token-driven (see src/styles/tokens.scss): no hardcoded colors. Replaces
 * bootstrap-vue's <b-avatar>. Shows an image (`src`), initials (`text`), or a
 * custom default slot (e.g. a feather-icon). The colored fill (when no image)
 * comes from the chosen role `variant`.
 *
 * `size` accepts a number (px), a named token (sm/md/lg/xl) or any raw CSS
 * length ('70px', '1.8rem') to match the old b-avatar call sites. `ring` adds a
 * surface-colored border for overlapping avatar stacks.
 *
 * VUE3-MIGRATION: `v-on="$listeners"` is the only Vue 2-ism here. In Vue 3
 * $listeners is removed (merged into $attrs); drop that line and v-bind="$attrs"
 * keeps forwarding listeners. The props/slots contract is already Vue 3-safe.
 */
const NAMED_SIZES = {
  sm: 28,
  md: 38,
  lg: 56,
  xl: 70,
}

export default {
  name: 'PlayAvatar',
  inheritAttrs: false,
  props: {
    src: {
      type: String,
      default: '',
    },
    /** Initials/acronym shown when there is no image */
    text: {
      type: String,
      default: '',
    },
    alt: {
      type: String,
      default: '',
    },
    /** number (px) | 'sm'|'md'|'lg'|'xl' | raw CSS length ('70px','1.8rem') */
    size: {
      type: [Number, String],
      default: 'md',
    },
    variant: {
      type: String,
      default: 'secondary',
      validator: v => ['primary', 'secondary', 'highlight', 'accent', 'ink', 'neutral'].includes(v),
    },
    /** Rounded square instead of a circle */
    square: {
      type: Boolean,
      default: false,
    },
    /** Surface-colored border for overlapping stacks */
    ring: {
      type: Boolean,
      default: false,
    },
    tag: {
      type: String,
      default: 'span',
    },
  },
  computed: {
    /** Resolve `size` to a CSS length and scale the font from it */
    resolvedSize() {
      const { size } = this
      if (typeof size === 'number') return `${size}px`
      if (NAMED_SIZES[size]) return `${NAMED_SIZES[size]}px`
      if (/^\d+$/.test(size)) return `${size}px`
      return size
    },
    avatarStyle() {
      return {
        '--av-size': this.resolvedSize,
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.play-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: var(--av-size, 38px);
  height: var(--av-size, 38px);
  border-radius: 50%;
  overflow: hidden;
  font-family: var(--font-display);
  font-weight: 800;
  /* font ≈ 40% of the avatar diameter */
  font-size: calc(var(--av-size, 38px) * .4);
  line-height: 1;
  text-decoration: none;
  user-select: none;

  &--square { border-radius: calc(var(--av-size, 38px) * .28); }

  &--ring {
    border: 2px solid var(--surface-bg);
    box-shadow: 0 0 0 1px var(--line);
  }

  &__img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  &__text {
    text-transform: uppercase;
    letter-spacing: -.02em;
  }

  /* ── Color fills (used when no image) ── */
  &--primary   { background: var(--c-primary);   color: var(--c-primary-on); }
  &--secondary { background: var(--c-secondary);  color: var(--c-secondary-on); }
  &--highlight { background: var(--c-highlight);   color: var(--c-highlight-on); }
  &--accent    { background: var(--c-accent);      color: var(--c-accent-on); }
  &--ink       { background: var(--surface-ink);   color: var(--text-on-ink); }
  &--neutral   { background: var(--line);          color: var(--text-strong); }
}
</style>
